import React, { useState, useEffect, useCallback } from 'react';
import { useWeb3 } from '../contexts/Web3Context';
import { ethers } from 'ethers';

const FACTORY_ABI = [
  "function getAllPresales() external view returns (tuple(address presaleAddress, address tokenAddress, address paymentAddress, address feeAddress, uint256 presalePrice, uint256 totalTokensForSale, uint256 feePercentage, uint256 createdAt, bool isActive)[] memory)",
  "function getActivePresales() external view returns (tuple(address presaleAddress, address tokenAddress, address paymentAddress, address feeAddress, uint256 presalePrice, uint256 totalTokensForSale, uint256 feePercentage, uint256 createdAt, bool isActive)[] memory)",
  "function getPresaleInfo(uint256 _presaleId) external view returns (tuple(address presaleAddress, address tokenAddress, address paymentAddress, address feeAddress, uint256 presalePrice, uint256 totalTokensForSale, uint256 feePercentage, uint256 createdAt, bool isActive) memory)",
  "function getPresalesByOwner(address _owner) external view returns (uint256[] memory)",
  "function presaleCount() external view returns (uint256)",
  "function owner() external view returns (address)",
  "function authorizedCreators(address _creator) external view returns (bool)",
  "function createPresale(address _tokenAddress, address _paymentAddress, address _feeAddress, uint256 _presalePrice, uint256 _totalTokensForSale, uint256 _feePercentage) external returns (address)",
  "function authorizeCreator(address _creator) external",
  "function deauthorizeCreator(address _creator) external",
  "function updatePresaleStatus(uint256 _presaleId, bool _isActive) external",
  "event PresaleCreated(uint256 indexed presaleId, address indexed presaleAddress, address indexed creator, address tokenAddress, uint256 presalePrice, uint256 totalTokensForSale)",
  "event CreatorAuthorized(address indexed creator)",
  "event CreatorDeauthorized(address indexed creator)"
];

const PRESALE_ABI = [
  "function startPresale() external",
  "function endPresale() external",
  "function distributeTokens() external",
  "function getPresaleInfo() external view returns (uint256 price, uint256 totalTokens, uint256 sold, uint256 remaining, bool active, bool distributed)",
  "function owner() external view returns (address)",
  "function emergencyWithdraw() external"
];

const ERC20_ABI = [
  "function decimals() external view returns (uint8)",
  "function symbol() external view returns (string)",
  "function name() external view returns (string)"
];

const AdminPanel = () => {
  const { account, provider, signer, isConnected } = useWeb3();
  const [isFactoryOwner, setIsFactoryOwner] = useState(false);
  const [isAuthorized, setIsAuthorized] = useState(false);
  const [presales, setPresales] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newPresaleData, setNewPresaleData] = useState({
    tokenAddress: '',
    paymentAddress: '',
    feeAddress: '',
    presalePrice: '',
    totalTokens: '',
    feePercentage: '',
    tokenDecimals: '18'
  });

  const getFactoryContract = () => {
    if (!signer) return null;
    const factoryAddress = process.env.REACT_APP_FACTORY_ADDRESS;
    if (!factoryAddress) {
      console.error('Factory address not configured. Please set REACT_APP_FACTORY_ADDRESS in your .env file.');
      return null;
    }
    return new ethers.Contract(factoryAddress, FACTORY_ABI, signer);
  };

  const getPresaleContract = (presaleAddress) => {
    if (!signer || !presaleAddress) return null;
    return new ethers.Contract(presaleAddress, PRESALE_ABI, signer);
  };

  const checkOwnership = useCallback(async () => {
    const factoryAddress = process.env.REACT_APP_FACTORY_ADDRESS;
    if (!provider || !account || !factoryAddress) return;

    try {
      const factory = new ethers.Contract(factoryAddress, FACTORY_ABI, provider);
      const factoryOwner = await factory.owner();
      const isAuth = await factory.authorizedCreators(account);
      
      setIsFactoryOwner(factoryOwner.toLowerCase() === account.toLowerCase());
      setIsAuthorized(isAuth);
    } catch (error) {
      console.error('Error checking ownership:', error);
    }
  }, [provider, account]);

  const loadPresales = useCallback(async () => {
    const factoryAddress = process.env.REACT_APP_FACTORY_ADDRESS;
    if (!provider || !factoryAddress) return;

    try {
      const factory = new ethers.Contract(factoryAddress, FACTORY_ABI, provider);
      const allPresales = await factory.getAllPresales();
      
      // Fetch token decimals for each presale
      const formattedPresales = await Promise.all(
        allPresales.map(async (presale, index) => {
          let tokenDecimals = 18; // Default to 18
          
          // Try to fetch actual token decimals
          if (presale.tokenAddress && presale.tokenAddress !== ethers.constants.AddressZero) {
            try {
              const tokenContract = new ethers.Contract(presale.tokenAddress, ERC20_ABI, provider);
              tokenDecimals = await tokenContract.decimals();
            } catch (error) {
              console.warn(`Failed to fetch decimals for token ${presale.tokenAddress}, using default 18:`, error);
              // Keep default 18 decimals
            }
          }
          
          return {
            id: index + 1,
            address: presale.presaleAddress,
            tokenAddress: presale.tokenAddress,
            paymentAddress: presale.paymentAddress,
            feeAddress: presale.feeAddress,
            price: ethers.utils.formatEther(presale.presalePrice), // Price is always in BNB (18 decimals)
            totalTokens: ethers.utils.formatUnits(presale.totalTokensForSale, tokenDecimals),
            tokenDecimals: tokenDecimals, // Store decimals for reference
            feePercentage: presale.feePercentage.toString(),
            createdAt: new Date(Number(presale.createdAt) * 1000).toLocaleString(),
            isActive: presale.isActive
          };
        })
      );

      setPresales(formattedPresales);
    } catch (error) {
      console.error('Error loading presales:', error);
      setError('Failed to load presales');
    }
  }, [provider]);

  const createPresale = async () => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Validate token decimals
      const tokenDecimals = parseInt(newPresaleData.tokenDecimals);
      if (isNaN(tokenDecimals) || tokenDecimals < 0 || tokenDecimals > 18) {
        setError('Token decimals must be a number between 0 and 18');
        setLoading(false);
        return;
      }

      const factory = getFactoryContract();
      
      const tx = await factory.createPresale(
        newPresaleData.tokenAddress,
        newPresaleData.paymentAddress,
        newPresaleData.feeAddress,
        ethers.utils.parseEther(newPresaleData.presalePrice),
        ethers.utils.parseUnits(newPresaleData.totalTokens, tokenDecimals),
        newPresaleData.feePercentage
      );
      
      setSuccess('Creating presale...');
      await tx.wait();
      setSuccess('Presale created successfully!');
      
      setNewPresaleData({
        tokenAddress: '',
        paymentAddress: '',
        feeAddress: '',
        presalePrice: '',
        totalTokens: '',
        feePercentage: '',
        tokenDecimals: '18'
      });
      setShowCreateForm(false);
      
      await loadPresales();
    } catch (error) {
      console.error('Error creating presale:', error);
      
      if (error.message.includes('user rejected')) {
        setError('Transaction was cancelled by user.');
      } else if (error.message.includes('Not authorized')) {
        setError('You are not authorized to create presales.');
      } else if (error.message.includes('Invalid token address')) {
        setError('Invalid token address provided.');
      } else {
        setError(`Failed to create presale: ${error.message || 'Unknown error occurred'}`);
      }
    } finally {
      setLoading(false);
    }
  };

  const startPresale = async (presaleAddress) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getPresaleContract(presaleAddress);
      
      const tx = await contract.startPresale();
      setSuccess('Starting presale...');
      await tx.wait();
      setSuccess('Presale started successfully!');
      
      await loadPresales();
    } catch (error) {
      console.error('Error starting presale:', error);
      setError(`Failed to start presale: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const endPresale = async (presaleAddress) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getPresaleContract(presaleAddress);
      
      const tx = await contract.endPresale();
      setSuccess('Ending presale...');
      await tx.wait();
      setSuccess('Presale ended successfully!');
      
      await loadPresales();
    } catch (error) {
      console.error('Error ending presale:', error);
      setError(`Failed to end presale: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const distributeTokens = async (presaleAddress) => {
    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getPresaleContract(presaleAddress);
      
      const tx = await contract.distributeTokens();
      setSuccess('Distributing tokens...');
      await tx.wait();
      setSuccess('Tokens distributed successfully!');
      
      await loadPresales();
    } catch (error) {
      console.error('Error distributing tokens:', error);
      setError(`Failed to distribute tokens: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  const emergencyWithdraw = async (presaleAddress) => {
    if (!window.confirm('Are you sure you want to perform emergency withdrawal? This will send all BNB to the owner.')) {
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const contract = getPresaleContract(presaleAddress);
      
      const tx = await contract.emergencyWithdraw();
      setSuccess('Emergency withdrawal...');
      await tx.wait();
      setSuccess('Emergency withdrawal completed!');
      
      await loadPresales();
    } catch (error) {
      console.error('Error emergency withdrawal:', error);
      setError(`Emergency withdrawal failed: ${error.message || 'Unknown error occurred'}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isConnected) {
      checkOwnership();
      loadPresales();
    }
  }, [isConnected, checkOwnership, loadPresales]);

  if (!isConnected) {
    return (
      <div className="admin-panel">
        <h2>Factory Admin Panel</h2>
        <p>Please connect your wallet to access admin functions.</p>
      </div>
    );
  }

  if (!process.env.REACT_APP_FACTORY_ADDRESS) {
    return (
      <div className="admin-panel">
        <h2>Factory Admin Panel</h2>
        <p>Factory address not configured. Please set REACT_APP_FACTORY_ADDRESS in your .env file.</p>
      </div>
    );
  }

  if (!isFactoryOwner && !isAuthorized) {
    return (
      <div className="admin-panel">
        <h2>Factory Admin Panel</h2>
        <p>You are not authorized to manage presales. Only factory owners and authorized creators can access this panel.</p>
      </div>
    );
  }

  return (
    <div className="admin-panel">
      <h2>Factory Admin Panel</h2>
      
      {error && <div className="error">{error}</div>}
      {success && <div className="success">{success}</div>}

      {/* Create New Presale Section */}
      <div className="create-presale-section">
        <h3>Create New Presale</h3>
        <button 
          className="create-button"
          onClick={() => setShowCreateForm(!showCreateForm)}
        >
          {showCreateForm ? 'Cancel' : 'Create New Presale'}
        </button>

        {showCreateForm && (
          <div className="create-form">
            <div className="form-group">
              <label>Token Address:</label>
              <input
                type="text"
                value={newPresaleData.tokenAddress}
                onChange={(e) => setNewPresaleData({...newPresaleData, tokenAddress: e.target.value})}
                placeholder="0x..."
              />
            </div>
            <div className="form-group">
              <label>Payment Address:</label>
              <input
                type="text"
                value={newPresaleData.paymentAddress}
                onChange={(e) => setNewPresaleData({...newPresaleData, paymentAddress: e.target.value})}
                placeholder="0x..."
              />
            </div>
            <div className="form-group">
              <label>Fee Address:</label>
              <input
                type="text"
                value={newPresaleData.feeAddress}
                onChange={(e) => setNewPresaleData({...newPresaleData, feeAddress: e.target.value})}
                placeholder="0x..."
              />
            </div>
            <div className="form-group">
              <label>Presale Price (BNB):</label>
              <input
                type="number"
                step="0.000001"
                value={newPresaleData.presalePrice}
                onChange={(e) => setNewPresaleData({...newPresaleData, presalePrice: e.target.value})}
                placeholder="0.1"
              />
            </div>
            <div className="form-group">
              <label>Total Tokens:</label>
              <input
                type="number"
                step="0.000001"
                value={newPresaleData.totalTokens}
                onChange={(e) => setNewPresaleData({...newPresaleData, totalTokens: e.target.value})}
                placeholder="1000000"
              />
            </div>
            <div className="form-group">
              <label>Token Decimals:</label>
              <input
                type="number"
                min="0"
                max="18"
                value={newPresaleData.tokenDecimals}
                onChange={(e) => setNewPresaleData({...newPresaleData, tokenDecimals: e.target.value})}
                placeholder="18"
              />
              <small style={{display: 'block', marginTop: '4px', color: '#666'}}>
                Number of decimals for the token (typically 18, but can be 6, 8, etc.)
              </small>
            </div>
            <div className="form-group">
              <label>Fee Percentage:</label>
              <input
                type="number"
                step="0.1"
                value={newPresaleData.feePercentage}
                onChange={(e) => setNewPresaleData({...newPresaleData, feePercentage: e.target.value})}
                placeholder="1"
              />
            </div>
            <button
              className="submit-button"
              onClick={createPresale}
              disabled={loading}
            >
              {loading && <span className="loading"></span>}
              Create Presale
            </button>
          </div>
        )}
      </div>

      {/* All Presales List */}
      <div className="presales-list">
        <h3>All Presales ({presales.length})</h3>
        {presales.length === 0 ? (
          <p>No presales found.</p>
        ) : (
          <div className="presales-grid">
            {presales.map((presale) => (
              <div key={presale.id} className="presale-card">
                <div className="presale-header">
                  <h4>Presale #{presale.id}</h4>
                  <span className={`status ${presale.isActive ? 'active' : 'inactive'}`}>
                    {presale.isActive ? 'Active' : 'Inactive'}
                  </span>
                </div>
                <div className="presale-info">
                  <div className="info-row">
                    <span>Address:</span>
                    <span className="address">{presale.address.slice(0, 6)}...{presale.address.slice(-4)}</span>
                  </div>
                  <div className="info-row">
                    <span>Price:</span>
                    <span>{presale.price} BNB</span>
                  </div>
                  <div className="info-row">
                    <span>Total Tokens:</span>
                    <span>{parseFloat(presale.totalTokens).toLocaleString()}</span>
                  </div>
                  <div className="info-row">
                    <span>Fee:</span>
                    <span>{presale.feePercentage}%</span>
                  </div>
                  <div className="info-row">
                    <span>Created:</span>
                    <span>{presale.createdAt}</span>
                  </div>
                </div>
                <div className="presale-actions">
                  <button
                    className="action-button start-button"
                    onClick={() => startPresale(presale.address)}
                    disabled={loading}
                  >
                    Start
                  </button>
                  <button
                    className="action-button end-button"
                    onClick={() => endPresale(presale.address)}
                    disabled={loading}
                  >
                    End
                  </button>
                  <button
                    className="action-button distribute-button"
                    onClick={() => distributeTokens(presale.address)}
                    disabled={loading}
                  >
                    Distribute
                  </button>
                  <button
                    className="action-button emergency-button"
                    onClick={() => emergencyWithdraw(presale.address)}
                    disabled={loading}
                  >
                    Emergency
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminPanel;
